/**
 * My Rentals -> [bot] (§7.1). Renter entry point: Console | Library | Activity.
 *
 * Character, Accounts, Schedule and Listing are deliberately not tabs here.
 * When a listing grants edit_character or edit_schedule those controls appear
 * inside the Console instead, so a renter never sees a nav branch that is mostly
 * locked to them.
 */

import { NavLink, Outlet, useParams } from 'react-router-dom';
import { accountFailureCopy } from '@pm/shared';

import { useApi } from '../../hooks/useApi';
import { useCapabilities } from '../../hooks/useCapabilities';
import type { Bot, Rental } from '../../api/types';
import { ErrorView } from '../../components/ErrorView';
import { Banner, Loading } from '../../components/ui';
import { duration, localDateTime } from '../../lib/format';
import type { BotOutlet } from '../botOutlet';
import { OWNER_LABEL } from '../../lib/vocabulary';
import { routes } from '../../api/routes';

export function RentalLayout() {
  const { rentalId = '' } = useParams();
  const rental = useApi<Rental>(routes.rentals.one(rentalId), { pollMs: 30_000 });
  const botId = rental.data?.bot_id ?? null;
  // §7.4 rule 5: 30s while the rental is live so a revocation lands within 60s.
  const bot = useApi<Bot>(botId ? routes.bots.one(botId) : null, {
    pollMs: rental.data?.status === 'active' ? 30_000 : 60_000,
  });
  const caps = useCapabilities(bot.data);

  if (rental.initialLoading) return <Loading label="Loading this rental" />;
  if (rental.problem) return <ErrorView problem={rental.problem} onRetry={rental.reload} />;
  if (!rental.data) return null;

  const r = rental.data;
  const ended = r.status !== 'active' && r.status !== 'scheduled';

  const tabs = [
    { to: 'console', label: 'Console' },
    /*
     * The renter gets the same Messages tab the owner does, on the same
     * condition — the server's `read_dm`. A rental that includes answering the
     * persona's enquiries and then hides the inbox is not the thing that was
     * rented; a rental that does not include it never sees the tab.
     */
    ...(caps.visible('read_dm') ? [{ to: 'dm', label: 'Messages' }] : []),
    { to: 'library', label: 'Library' },
    { to: 'activity', label: 'Activity' },
    ...(r.pending_consent ? [{ to: 'consent', label: 'Consent request' }] : []),
    ...(r.review?.can_submit ? [{ to: 'review', label: 'Review' }] : []),
  ];

  return (
    <div className="bot-scope">
      <header className="scope-head">
        <h1>{r.bot_name}</h1>
        <p className="muted">
          {OWNER_LABEL} @{r.owner_handle} · {localDateTime(r.start_at)} –{' '}
          {localDateTime(r.end_at)} · {r.status.replace(/_/g, ' ')}
        </p>
      </header>

      {r.status === 'active' ? (
        <Banner tone="info" title="Your access is live">
          {duration(Math.max(0, (new Date(r.end_at).getTime() - Date.now()) / 1000))} remaining.
          Access ends automatically when the rental does.
        </Banner>
      ) : null}

      {/*
        §5.3 TERMINATED_ACCOUNT_FAILURE. The copy is fixed and rendered verbatim:
        it is the platform saying, in the one situation where a renter loses time
        they arranged to hold, that it holds no money and can refund nothing.
      */}
      {r.status === 'terminated_account_failure' && r.termination_detail ? (
        <Banner tone="bad" title="This rental ended early" role="alert">
          <span className="approval-copy">
            {accountFailureCopy({
              botName: r.bot_name,
              platform: r.termination_detail.platform,
              timestamp: localDateTime(r.termination_detail.timestamp),
              daysEarly: r.termination_detail.days_early,
              actionCount: r.termination_detail.action_count,
              downloadWindowDays: r.termination_detail.download_window_days,
            })}
          </span>
        </Banner>
      ) : ended ? (
        <Banner tone="warn" title="This rental has ended">
          Your permissions were withdrawn when it ended. The activity log stays readable.
        </Banner>
      ) : null}

      <nav className="subnav" aria-label="Rental sections">
        <ul>
          {tabs.map((t) => (
            <li key={t.to}>
              <NavLink
                to={t.to}
                className={({ isActive }) => `subnav-link${isActive ? ' subnav-link-active' : ''}`}
              >
                {t.label}
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>

      {bot.initialLoading ? <Loading label="Loading the bot" /> : null}
      {bot.problem ? <ErrorView problem={bot.problem} onRetry={bot.reload} /> : null}
      {bot.data ? (
        <Outlet
          context={
            {
              bot: bot.data,
              caps,
              reload: () => {
                bot.reload();
                rental.reload();
              },
              scope: 'renter',
              rental: r,
            } satisfies BotOutlet
          }
        />
      ) : null}
    </div>
  );
}
